import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { getDashboard } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { ExportButtons } from "@/components/ExportButtons";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
  PieChart, Pie,
} from "recharts";
import {
  ClipboardList, Activity, Truck, Building2, Route, DollarSign, MapPin, Users,
} from "lucide-react";

const KPI = ({ label, value, icon: Icon, accent = "#0055FF", testid, to }) => {
  const inner = (
    <Card data-testid={testid} className={`p-5 border-border/60 shadow-none transition-all bg-white ${to ? "hover:shadow-md hover:-translate-y-0.5 cursor-pointer" : ""}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground font-semibold">{label}</div>
          <div className="mt-2 font-display font-extrabold text-3xl tabular-nums">{value}</div>
          {to && <div className="mt-1 text-[11px] text-[#0055FF] font-medium">View details →</div>}
        </div>
        <div className="h-9 w-9 rounded-md grid place-items-center" style={{ background: `${accent}15` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
      </div>
    </Card>
  );
  return to ? <Link to={to} data-testid={`${testid}-link`}>{inner}</Link> : inner;
};

const STATUS_COLORS = {
  Draft: "#a3a3a3", Submitted: "#3b82f6", Approved: "#16a34a", Scheduled: "#8b5cf6",
  "In Progress": "#f59e0b", Completed: "#0a0a0a", Cancelled: "#dc2626",
};

const fmtMoney = (v) => (v || v === 0 ? `$${Number(v).toLocaleString()}` : "—");

export default function Dashboard() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["dashboard"], queryFn: getDashboard });
  const d = data || {};
  const sc = d.status_counts || {};
  const statusData = Object.keys(sc).map((k) => ({ name: k, value: sc[k] }));
  const pc = d.priority_counts || {};
  const priorityData = Object.keys(pc).map((k) => ({ name: k, value: pc[k] }));

  return (
    <Layout>
      <PageHeader title="Operations Dashboard" subtitle="Logistics & driver operations overview">
        <ExportButtons collection="dashboard" excelOnly />
      </PageHeader>
      <div className="p-8 space-y-6 animate-fade-up">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI label="Total Records" value={d.total_records ?? "—"} icon={ClipboardList} testid="kpi-total" to="/master-log" />
          <KPI label="Active Records" value={d.active_records ?? "—"} icon={Activity} accent="#f59e0b" testid="kpi-active" to="/master-log?active=1" />
          <KPI label="Internal Jobs" value={d.internal_jobs ?? "—"} icon={Truck} accent="#16a34a" testid="kpi-internal" to={`/master-log?source=${encodeURIComponent("Internal Asset")}`} />
          <KPI label="Third-Party Jobs" value={d.third_party_jobs ?? "—"} icon={Building2} accent="#8b5cf6" testid="kpi-thirdparty" to={`/master-log?source=${encodeURIComponent("Third-Party Asset")}`} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 p-6 border-border/60 shadow-none bg-white">
            <h3 className="font-display font-bold text-lg mb-4">Records by Status</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={statusData} margin={{ left: -20 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: "#f5f5f5" }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} className="cursor-pointer"
                  onClick={(e) => e?.name && navigate(`/master-log?status=${encodeURIComponent(e.name)}`)}>
                  {statusData.map((e) => <Cell key={e.name} fill={STATUS_COLORS[e.name] || "#0055FF"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-6 border-border/60 shadow-none bg-white">
            <h3 className="font-display font-bold text-lg mb-4">Priority Mix</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={priorityData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}
                  className="cursor-pointer" onClick={(e) => e?.name && navigate(`/master-log?priority=${encodeURIComponent(e.name)}`)}>
                  {priorityData.map((e, i) => (
                    <Cell key={e.name} fill={["#a3a3a3", "#3b82f6", "#f59e0b", "#dc2626"][i]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-3 mt-2 justify-center">
              {priorityData.map((e, i) => (
                <div key={e.name} className="flex items-center gap-1.5 text-xs">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: ["#a3a3a3", "#3b82f6", "#f59e0b", "#dc2626"][i] }} />
                  {e.name} ({e.value})
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPI label="Trip Records" value={d.total_trips ?? "—"} icon={Route} accent="#0055FF" testid="kpi-trips" to="/trips" />
          <KPI label="Total Miles" value={d.total_miles ? d.total_miles.toLocaleString() : "—"} icon={MapPin} accent="#0891b2" testid="kpi-miles" to="/trips" />
          <KPI label="Drivers / Assets" value={`${d.drivers ?? 0} / ${d.assets ?? 0}`} icon={Users} accent="#8b5cf6" testid="kpi-fleet" to="/drivers" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI label="Internal Trip Cost" value={fmtMoney(d.internal_trip_cost)} icon={Truck} accent="#16a34a" testid="kpi-internal-cost" to="/trips" />
          <KPI label="Third-Party Trip Cost" value={fmtMoney(d.third_party_trip_cost)} icon={Building2} accent="#8b5cf6" testid="kpi-thirdparty-cost" to="/trips" />
          <KPI label="Dead Leg Cost" value={fmtMoney(d.dead_leg_cost)} icon={Route} accent="#f59e0b" testid="kpi-deadleg-cost" to="/master-log?deadleg=1" />
          <KPI label="Total Trip Cost" value={fmtMoney(d.total_trip_cost)} icon={DollarSign} accent="#0a0a0a" testid="kpi-cost" to="/trips" />
        </div>
      </div>
    </Layout>
  );
}
